import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"

/**
 * Ancres du centre d'aide. Les liens « Comment ça marche ? » des pages pointent
 * vers `/help#<ancre>` : l'ancre ne doit pas dépendre du libellé d'une section,
 * sinon le moindre accent ou tiret change l'id et le lien tombe en haut de page
 * (`#faq-emargement` visait « faq-formation-emargement »). Chaque section de
 * `help-faq.json` a donc un id explicite ; `help-anchors.test.ts` vérifie que
 * toute section en a un et que tout lien `/help#…` du code en vise un.
 */
export const HELP_SECTION_ANCHORS: Readonly<Record<string, string>> = {
  "Tableau de bord": "faq-tableau-de-bord",
  "CRM — Gestion de la relation client": "faq-crm",
  "Formation — Programmes": "faq-programmes-de-formation",
  "Formation — Sessions et calendrier": "faq-sessions",
  "Formation — Inscriptions et pipeline": "faq-inscriptions-et-pipeline",
  "Formation — Émargement": "faq-emargement",
  "Formation — Jurys, blocs de compétences et VAE": "faq-jurys-et-vae",
  "Commercial — Devis et factures": "faq-devis-et-factures",
  "Commercial — Contrats et OPCO": "faq-contrats-et-opco",
  "RH — Personnel et formateurs": "faq-personnel-et-formateurs",
  "RH — Sous-traitants": "faq-sous-traitants",
  "Qualiopi — Indicateurs et évaluations": "faq-qualiopi-indicateurs",
  "Qualiopi — Réclamations et améliorations": "faq-reclamations-et-ameliorations",
  "Qualiopi — Veille, enquêtes et lieux": "faq-veille-enquetes-et-lieux",
  "Documents": "faq-documents",
  "Messages": "faq-messages",
  "Rapports": "faq-rapports",
  "Administration": "faq-administration",
  "Paramètres": "faq-parametres",
  "Multi-organisation": "faq-multi-organisation",
  "Qualiopi — Les 7 critères en détail": "faq-qualiopi-criteres",
}

/**
 * Ancres de questions précises, pour un lien qui vise une réponse plutôt
 * qu'une section : la page d'aide ouvre la réponse visée.
 */
export const HELP_QUESTION_ANCHORS: Readonly<Record<string, string>> = {
  "Comment gérer les abandons de formation ?": "faq-abandons",
  "Comment monter un dossier de financement OPCO ?": "faq-dossier-opco",
}

function slugify(title: string): string {
  return title
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
}

function lookup(table: Readonly<Record<string, string>>, key: string): O.Option<string> {
  return Object.prototype.hasOwnProperty.call(table, key) ? O.some(table[key]) : O.none
}

/**
 * Id d'une section du centre d'aide. Une section ajoutée sans id explicite
 * reste joignable par son libellé (le test la signale pour qu'on lui en donne un).
 */
export function helpSectionAnchor(title: string): string {
  return pipe(
    lookup(HELP_SECTION_ANCHORS, title),
    O.getOrElse(() => `faq-${slugify(title)}`),
  )
}

/** Id d'une question visée par un lien, s'il en a un. */
export function helpQuestionAnchor(question: string): O.Option<string> {
  return lookup(HELP_QUESTION_ANCHORS, question)
}
